const SUFFIX_PATTERN = /^(.*?)(#[A-Z])$/i;

const CONTROL_TYPE_BY_SUFFIX = {
  '#C': 'checkbox',
  '#D': 'select',
};

function cloneIr(ir) {
  return JSON.parse(JSON.stringify(ir));
}

function parseTechnicalSuffix(variableName) {
  const match = (variableName || '').match(SUFFIX_PATTERN);

  if (!match) {
    return {
      variable: variableName,
      suffix: null,
      controlType: null,
    };
  }

  const suffix = match[2].toUpperCase();

  return {
    variable: match[1],
    suffix,
    controlType: CONTROL_TYPE_BY_SUFFIX[suffix] || null,
  };
}

function normalizeName(name) {
  return (name || '').replace(/#[A-Z]$/i, '').toUpperCase();
}

function findCatalog(catalogs, variableName) {
  const normalizedVariable = normalizeName(variableName);

  return Object.values(catalogs).find((catalog) => normalizeName(catalog.variable) === normalizedVariable) || null;
}

function applyControlType(field, catalogs) {
  if (field.type === 'display' || field.control === 'display' || field.controlType === 'label') {
    const suffixInfo = parseTechnicalSuffix(field.variable);

    return {
      ...field,
      variable: suffixInfo.variable,
      variableBase: suffixInfo.variable,
      name: field.name || suffixInfo.variable,
      binding: field.binding || suffixInfo.variable,
      suffix: suffixInfo.suffix,
      control: 'display',
      controlType: 'label',
    };
  }

  if (field.type === 'field_row') {
    return {
      ...field,
      control: 'field_row',
      controlType: 'field_row',
      parts: (field.parts || []).map((part) => (part.type === 'field' || part.type === 'select' ? applyControlType(part, catalogs) : part)),
    };
  }

  if (field.type === 'checkbox-group') {
    return {
      ...field,
      control: 'checkbox-group',
      controlType: 'checkbox-group',
      options: (field.options || []).map((option) => {
        const suffixInfo = parseTechnicalSuffix(option.variable);

        return {
          ...option,
          variable: suffixInfo.variable,
          variableBase: suffixInfo.variable,
          suffix: suffixInfo.suffix,
          controlType: suffixInfo.controlType || 'checkbox',
        };
      }),
    };
  }

  if (!field.variable || /^@/i.test(field.variable)) {
    return field;
  }

  const suffixInfo = parseTechnicalSuffix(field.variable);
  const catalog = findCatalog(catalogs, suffixInfo.variable);
  const controlType = suffixInfo.controlType || field.controlType || field.control || field.type;
  const nextField = {
    ...field,
    variable: suffixInfo.variable,
    variableBase: suffixInfo.variable,
    name: field.name || suffixInfo.variable,
    binding: field.binding || suffixInfo.variable,
    value: field.value ?? '',
    templateValue: field.templateValue || `{${suffixInfo.variable}}`,
    placeholder: field.placeholder || suffixInfo.variable,
    suffix: suffixInfo.suffix,
    controlType,
  };

  if (suffixInfo.suffix === '#D') {
    nextField.type = 'select';
    nextField.control = 'select';
    nextField.catalog = catalog ? catalog.id : null;
    nextField.catalogMissing = !catalog;
    nextField.options = catalog ? [...catalog.options] : [];
    return nextField;
  }

  if (suffixInfo.suffix === '#C') {
    nextField.type = 'field';
    nextField.control = 'checkbox';
    nextField.catalogMissing = false;
    return nextField;
  }

  return nextField;
}

function getCheckboxPrefix(variableName) {
  const withoutTrailingNumber = variableName.replace(/[0-9]+$/i, '');
  const withoutTrailingUnderscoreNumber = variableName.replace(/_[0-9]+$/i, '');

  if (withoutTrailingUnderscoreNumber !== variableName) {
    return withoutTrailingUnderscoreNumber;
  }

  if (withoutTrailingNumber !== variableName) {
    return withoutTrailingNumber;
  }

  return variableName;
}

function buildCheckboxGroups(forms) {
  return forms.flatMap((form) => {
    const groups = {};
    const structuralGroups = form.fields
      .filter((field) => field.type === 'checkbox-group')
      .map((field) => ({
        type: 'checkboxGroup',
        formId: form.id,
        label: field.label,
        mutuallyExclusiveCandidate: true,
        fields: (field.options || []).map((option) => ({
          label: option.label,
          variable: option.variable,
          suffix: option.suffix,
          line: option.line,
        })),
      }));

    form.fields
      .filter((field) => field.controlType === 'checkbox')
      .forEach((field) => {
        const prefix = getCheckboxPrefix(field.variableBase || field.variable);

        if (!groups[prefix]) {
          groups[prefix] = {
            type: 'checkboxGroupCandidate',
            formId: form.id,
            prefix,
            mutuallyExclusiveCandidate: false,
            fields: [],
          };
        }

        groups[prefix].fields.push({
          label: field.label,
          variable: field.variable,
          suffix: field.suffix,
          line: field.line,
        });
      });

    return [
      ...structuralGroups,
      ...Object.values(groups)
      .filter((group) => group.fields.length > 1)
      .map((group) => ({
        ...group,
        mutuallyExclusiveCandidate: true,
      })),
    ];
  });
}

export function analyzeControlTypes(ir) {
  const nextIr = cloneIr(ir);

  nextIr.forms = nextIr.forms.map((form) => ({
    ...form,
    fields: form.fields.map((field) => applyControlType(field, nextIr.catalogs)),
  }));
  nextIr.checkboxGroups = buildCheckboxGroups(nextIr.forms);

  return nextIr;
}
